// Editing territorial control boundaries by hand: the drag, and the cut.
//
// The district is the unit the FEED can move (territory.ts's Reassign), but a
// curator with a better picture than the district grid needs to push the line
// itself. Two gestures live here:
//
//   THE DRAG (`editTerritoryBoundary`). Every territory of the country loads
//   into the editor at once, and a vertex is TOPOLOGICAL: the same coordinate
//   on two (or three) outlines is one vertex, and dragging it moves every
//   outline that carries it, so a shared line moves as one line and the two
//   sides never overlap or gap on screen. A click on the line adds a vertex to
//   every outline sharing that segment; a tap or right-click on a vertex
//   selects it for Delete, and deleting walks the selection on to the next
//   vertex so a run of them goes with a run of presses. Finish hands back
//   every outline that changed, in one request (POST /api/territory/reshape
//   `territories`), and the server resolves what the move means for ground:
//   taken from a neighbour, handed to one, or released — so a one-sided edit
//   (a coast, or a vertex only one outline had) still lands right.
//
//   THE CUT (`captureLine` + territory.ts's split flow). A line drawn from
//   outside a territory to outside it is a NEW boundary: the server cuts the
//   territory with it (POST /api/territory/split), previews the pieces with
//   the neighbour across each, and the piece the operator picks moves to
//   the holder they name — the same operation a district reassignment is,
//   over the piece's outline.
//
// The territories are country-sized MultiPolygons with tens of thousands of
// vertices, which shapes the rendering:
//
//   * The outline is CHUNKED — one LineString feature per CHUNK vertices —
//     and an edit re-sends only the chunks it touched (GeoJSONSource.updateData
//     by feature id). Re-sending the whole outline after every drop was a
//     visible stall on a country.
//   * The vertex handles are only the ones IN VIEW (re-cut on moveend), and
//     none at all when the view holds more than HANDLES_MAX — at theatre zoom
//     every vertex of a coastline sits on the same pixel and the ring is
//     unreadable anyway. The line itself can be grabbed at any zoom.
//   * What moves during a drag is a third, tiny source — the vertex and its
//     neighbouring segments on every outline. Nothing country-sized repaints
//     per frame.
//   * Each outline keeps the ENVELOPE of everything the edit moved on it, so
//     the server diffs old and new inside that box instead of across the
//     whole country.
//
// Pointer events, not MapLibre's mouse events (rotate.ts's lesson: a finger
// never raises those), and the draw bar for Finish/Undo/Cancel (drawbar.ts:
// a gesture the platform reserves is not a way out). Deleting has two doors
// for the same reason: right-click on a mouse, or tap the vertex and press
// the chip that appears beside it (or the Delete key).

import * as maplibregl from 'maplibre-gl';
import type { EntityDto } from './api';
import { get, post } from './api';
import { showDrawBar, type DrawBar } from './drawbar';
import { fmtKm2 } from './geo';
import { toast } from './hud';
import {
  buildSnapIndex, chooseSide, falloff, graftRun, nearestSnap, nearestVertex, traceNetwork,
  type Guide, type ScreenPt, type SnapHit, type SnapIndex,
} from './snap';
import { createGuideSet, installGuideLayers, type GuideSet } from './guides';
import { bumpTiles, forceTileRefresh } from './objects';

const SRC_SHAPE = 'terr-edit-shape';
const SRC_VERTS = 'terr-edit-verts';
const SRC_DRAG = 'terr-edit-drag';
const SRC_CUT = 'terr-cut';
const SRC_PIECES = 'terr-pieces';
const SRC_GUIDE = 'terr-guide';
const L_CASING = 'terr-edit-casing';
const L_LINE = 'terr-edit-line';
const L_VERTS = 'terr-edit-verts';
const L_DRAG_LINE = 'terr-edit-drag-line';
const L_DRAG_VERT = 'terr-edit-drag-vert';
const L_SEL_VERT = 'terr-edit-sel-vert';
const L_CUT_LINE = 'terr-cut-line';
const L_CUT_VERTS = 'terr-cut-verts';
const L_GUIDE = 'terr-guide-line';
const L_SNAP = 'terr-snap';
const L_PIECE_FILL = 'terr-piece-fill';
const L_PIECE_LINE = 'terr-piece-line';
const L_PIECE_LABEL = 'terr-piece-label';
/** Below this the handles are hidden — the line can still be grabbed. */
export const HANDLE_MINZOOM = 6;
/** More vertices than this in view and none are drawn: zoom in. */
export const HANDLES_MAX = 4000;
/** Vertices per outline chunk — the unit an edit re-sends. */
export const CHUNK = 200;
/** A press that moves less than this is a tap (select), not a drag. */
const TAP_PX = { mouse: 3, touch: 10 };
/** How near the pointer has to be before a vertex jumps to a line: about what
 *  a hand means by "on it", and the same at every zoom because it is pixels. */
export const SNAP_PX = 12;
/** Tracing aims at a line rather than dragging onto it, so it forgives more. */
export const TRACE_PX = 16;
/** Past this many vertices in view, indexing them every drag costs more than
 *  the snap is worth; snapping goes quiet rather than making the drag stutter. */
export const SNAP_MAX_PTS = 150_000;
/** The push brush's radius steps, in pixels — `[` and `]` walk them. */
export const BRUSH_RADII = [16, 28, 44, 70, 110];
/** A pointer that moved less than this in a frame pushes nothing yet. */
const PUSH_MIN_PX = 0.75;

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

export type Pt = [number, number];
export type Env = [number, number, number, number];
/** One ring, OPEN (no closing point), in chunks of ~CHUNK vertices, with
 *  where it came from so the MultiPolygon rebuilds in the shape it arrived. */
export interface Ring { poly: number; ring: number; chunks: Pt[][] }
/** A vertex address: territory, ring, chunk, index within the chunk. */
export interface VRef { t: number; r: number; c: number; i: number }
/** One territory under edit. */
export interface Terr {
  entity: EntityDto; wasPolygon: boolean; rings: Ring[];
  /** Envelope of every vertex moved/added/removed on THIS outline, with
   *  their neighbours: the box the server diffs inside. Null = untouched. */
  env: Env | null;
}
/** One undo step. A move remembers where the vertex was; an insert is
 *  removed again; a delete puts the point back at every address. */
type Step = { kind: 'move'; refs: VRef[]; prev: Pt; pos: Pt }
  | { kind: 'insert'; refs: VRef[] }
  | { kind: 'delete'; refs: VRef[]; prev: Pt }
  /** A traced run replaced a span of a ring, or a push stroke bent and
   *  densified several: undo puts those rings back whole, CHUNK FOR CHUNK as
   *  they were — re-chunking a flat copy would shift the addresses every
   *  earlier step on the stack still holds. */
  | { kind: 'rings'; rings: RingSnapshot[] };

export interface RingSnapshot { t: number; r: number; chunks: Pt[][] }

/** A guide that knows which outline it came from, so tracing a line onto
 *  itself is skipped. */
type EditGuide = Guide & { terr?: { t: number; r: number } };

export interface ReshapeResult {
  id: string | null; ids?: string[]; updated: string[]; removed: string[]; rev: number | null;
  gainedKm2: number; vacatedKm2: number; releasedKm2: number;
}

export interface EditHooks {
  /** After a successful save (tiles are bumped here). */
  onSaved: (res: ReshapeResult) => void;
  /** Editing is over, saved or not — reopen whatever the card was. */
  onDone: () => void;
}

/** The outlines and their vertex index — what the topological operations
 *  work on; the editor's State is this plus the map-side bookkeeping. */
export interface Model {
  terrs: Terr[];
  /** coordinate key → every address carrying that vertex. */
  index: Map<string, VRef[]>;
}

interface State extends Model {
  steps: Step[];
  bar: DrawBar;
  drag: { refs: VRef[]; pos: Pt; prev: Pt; inserted: boolean; pointerId: number;
          start: [number, number]; tapPx: number; snapped: SnapHit | null } | null;
  /** A tapped vertex, waiting for Delete. */
  selected: VRef | null;
  /** Too many vertices in view to draw handles. */
  crowded: number;
  saving: boolean;
  /** Lines to snap and trace against, in the camera's screen coordinates —
   *  thrown away whenever the map moves. Null means "rebuild on demand". */
  guides: EditGuide[] | null;
  snapIndex: SnapIndex | null;
  snapping: boolean;
  tracing: boolean;
  /** Where a trace started; the next click on the same line ends it. */
  traceFrom: SnapHit | null;
  /** Admin linework and roads for the view (guides.ts). */
  lines: GuideSet | null;
  /** New guide lines landed mid-drag: re-index once the vertex is dropped. */
  guidesStale: boolean;
  /** The push brush is armed: a press pushes the line instead of grabbing a vertex. */
  pushing: boolean;
  radiusPx: number;
  cleanup: () => void;
}

let state: State | null = null;
let cancelPendingEdit: (() => void) | null = null;
let installed = false;
/** The country's own ADM0 rings, open, loaded once per edit. The line the
 *  server clips gained ground to, so snapping to it snaps to what will be
 *  kept. */
let countryRings: Pt[][] = [];
let chip: HTMLButtonElement | null = null;
/** Line capture or piece picking in progress: clicks belong to it. */
let otherMode = 0;

/** Any boundary-editing gesture owns the map's clicks right now. */
export function isTerritoryEditActive(): boolean {
  return state !== null || otherMode > 0;
}

/** Another boundary gesture (territoryarea.ts's Mark area) owns the map's
 *  clicks until the returned release is called — main.ts asks
 *  isTerritoryEditActive before opening a card. */
export function holdMapClicks(): () => void {
  otherMode++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    otherMode--;
  };
}

// --- rings: split, address, edit, rebuild -------------------------------------

/** Split a Polygon/MultiPolygon into open, chunked rings. */
export function ringsOf(geometry: EntityDto['geometry']): Ring[] {
  if (!geometry) return [];
  const polys: Pt[][][] = geometry.type === 'Polygon'
    ? [geometry.coordinates as Pt[][]]
    : geometry.type === 'MultiPolygon' ? geometry.coordinates as Pt[][][] : [];
  const out: Ring[] = [];
  polys.forEach((rings, poly) => rings.forEach((ring, ri) => {
    const pts = ring.map((c): Pt => [c[0]!, c[1]!]);
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (pts.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]) pts.pop();
    if (pts.length < 3) return;
    const chunks: Pt[][] = [];
    for (let i = 0; i < pts.length; i += CHUNK) chunks.push(pts.slice(i, i + CHUNK));
    out.push({ poly, ring: ri, chunks });
  }));
  return out;
}

/** The ring's vertices in order, chunks joined. */
export function flatRing(ring: Ring): Pt[] {
  return ring.chunks.flat();
}

export function ringSize(ring: Ring): number {
  let n = 0;
  for (const ch of ring.chunks) n += ch.length;
  return n;
}

/** Rebuild the geometry: every ring closed, polygons in their original
 *  order, a Polygon when that is what came in. */
export function geometryOf(rings: Ring[], asPolygon: boolean): GeoJSON.Polygon | GeoJSON.MultiPolygon {
  const polys = new Map<number, Pt[][]>();
  for (const r of rings) {
    const list = polys.get(r.poly) ?? [];
    const pts = flatRing(r);
    list[r.ring] = [...pts, pts[0]!];
    polys.set(r.poly, list);
  }
  const coords = [...polys.keys()].sort((a, b) => a - b)
    .map((k) => polys.get(k)!.filter(Boolean));
  if (asPolygon && coords.length === 1) return { type: 'Polygon', coordinates: coords[0]! };
  return { type: 'MultiPolygon', coordinates: coords };
}

/** The identity of a vertex across outlines: its exact coordinate. Two
 *  territories built from one boundary set share bit-identical points. */
export function keyOf(p: Pt): string {
  return `${p[0]},${p[1]}`;
}

function ringAt(terrs: Terr[], v: VRef): Ring {
  return terrs[v.t]!.rings[v.r]!;
}

function ptAt(terrs: Terr[], v: VRef): Pt {
  return ringAt(terrs, v).chunks[v.c]![v.i]!;
}

/** The vertex before/after `v` along its ring, across chunk joins and the
 *  closing edge. Skips empty chunks (a delete can leave one). */
export function neighbours(ring: Ring, v: { c: number; i: number }): { before: Pt; after: Pt } {
  const step = (c: number, i: number, dir: 1 | -1): Pt => {
    let cc = c;
    let ii = i + dir;
    for (let guard = 0; guard < ring.chunks.length + 1; guard++) {
      const ch = ring.chunks[cc]!;
      if (ii >= 0 && ii < ch.length) return ch[ii]!;
      cc = (cc + dir + ring.chunks.length) % ring.chunks.length;
      ii = dir === 1 ? 0 : ring.chunks[cc]!.length - 1;
    }
    return ring.chunks[c]![i]!;
  };
  return { before: step(v.c, v.i, -1), after: step(v.c, v.i, 1) };
}

/** Insert `pt` AFTER the vertex at `v`; returns the new vertex's address. */
export function insertAfter(ring: Ring, v: VRef, pt: Pt): VRef {
  ring.chunks[v.c]!.splice(v.i + 1, 0, pt);
  return { t: v.t, r: v.r, c: v.c, i: v.i + 1 };
}

/** Remove the vertex at `v`; returns it. Refuses (null) when the ring would
 *  drop below three corners. */
export function removeAt(ring: Ring, v: { c: number; i: number }): Pt | null {
  if (ringSize(ring) <= 3) return null;
  return ring.chunks[v.c]!.splice(v.i, 1)[0] ?? null;
}

/** Index of the previous NON-EMPTY chunk (whose line ends at chunk c's first
 *  vertex), cyclically. */
function prevChunk(ring: Ring, c: number): number {
  for (let k = 1; k <= ring.chunks.length; k++) {
    const cc = (c - k + ring.chunks.length) % ring.chunks.length;
    if (ring.chunks[cc]!.length) return cc;
  }
  return c;
}

/** The LineString a chunk draws: its vertices plus the first vertex of the
 *  next non-empty chunk, so the joins and the closing edge are drawn once.
 *  Null for an empty chunk. */
export function chunkLine(ring: Ring, c: number): Pt[] | null {
  const ch = ring.chunks[c]!;
  if (!ch.length) return null;
  let next = c;
  for (let k = 1; k <= ring.chunks.length; k++) {
    const cc = (c + k) % ring.chunks.length;
    if (ring.chunks[cc]!.length) { next = cc; break; }
  }
  return [...ch, ring.chunks[next]![0]!];
}

/** The segment nearest a screen point: the address of its START vertex, by
 *  perpendicular distance in pixels. Walks every segment of the ring
 *  including the closing one. */
export function nearestSegment(map: { project: (ll: [number, number]) => { x: number; y: number } },
                               ring: Ring, at: [number, number]): { c: number; i: number } | null {
  const refs: { c: number; i: number }[] = [];
  const proj: { x: number; y: number }[] = [];
  ring.chunks.forEach((ch, c) => ch.forEach((p, i) => {
    refs.push({ c, i });
    proj.push(map.project(p));
  }));
  if (proj.length < 2) return null;
  let best = -1;
  let bestD = Infinity;
  for (let k = 0; k < proj.length; k++) {
    const a = proj[k]!;
    const b = proj[(k + 1) % proj.length]!;
    const d = segDist(at[0], at[1], a.x, a.y, b.x, b.y);
    if (d < bestD) { bestD = d; best = k; }
  }
  return best < 0 ? null : refs[best]!;
}

function segDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** Grow an envelope by points. */
export function extendEnv(env: Env | null, ...pts: Pt[]): Env | null {
  let out = env ? [...env] as Env : null;
  for (const p of pts) {
    if (!out) { out = [p[0], p[1], p[0], p[1]]; continue; }
    out[0] = Math.min(out[0], p[0]); out[1] = Math.min(out[1], p[1]);
    out[2] = Math.max(out[2], p[0]); out[3] = Math.max(out[3], p[1]);
  }
  return out;
}

/** Ray-cast point-in-polygon over a Polygon/MultiPolygon (holes honoured). */
export function containsPoint(geometry: EntityDto['geometry'], p: Pt): boolean {
  if (!geometry) return false;
  const polys: Pt[][][] = geometry.type === 'Polygon'
    ? [geometry.coordinates as Pt[][]]
    : geometry.type === 'MultiPolygon' ? geometry.coordinates as Pt[][][] : [];
  const inRing = (ring: Pt[]): boolean => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i]!;
      const [xj, yj] = ring[j]!;
      if ((yi > p[1]) !== (yj > p[1])
          && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  return polys.some((rings) => rings.length > 0 && inRing(rings[0]!)
    && !rings.slice(1).some(inRing));
}

// --- the topological index -----------------------------------------------------

/** Every vertex of every outline, by coordinate. */
export function buildIndex(terrs: Terr[]): Map<string, VRef[]> {
  const index = new Map<string, VRef[]>();
  terrs.forEach((terr, t) => terr.rings.forEach((ring, r) => ring.chunks.forEach((ch, c) =>
    ch.forEach((p, i) => {
      const k = keyOf(p);
      const list = index.get(k);
      if (list) list.push({ t, r, c, i }); else index.set(k, [{ t, r, c, i }]);
    }))));
  return index;
}

/** Re-address one chunk after a splice shifted its indices. Stale entries
 *  for a REMOVED point are the caller's to drop (it knows the point). */
function reindexChunk(s: Model, t: number, r: number, c: number): void {
  const ch = s.terrs[t]!.rings[r]!.chunks[c]!;
  ch.forEach((p, i) => {
    const k = keyOf(p);
    const kept = (s.index.get(k) ?? []).filter((v) => !(v.t === t && v.r === r && v.c === c));
    kept.push({ t, r, c, i });
    s.index.set(k, kept);
  });
}

export function refsAt(s: Model, p: Pt): VRef[] {
  return s.index.get(keyOf(p)) ?? [];
}

function touch(s: Model, t: number, ...pts: Pt[]): void {
  const terr = s.terrs[t]!;
  terr.env = extendEnv(terr.env, ...pts);
}

/** Move every address of one vertex to `pos`. */
export function moveVertex(s: Model, refs: VRef[], prev: Pt, pos: Pt): void {
  s.index.delete(keyOf(prev));
  for (const v of refs) {
    const ring = ringAt(s.terrs, v);
    const { before, after } = neighbours(ring, v);
    ring.chunks[v.c]![v.i] = [pos[0], pos[1]];
    touch(s, v.t, prev, pos, before, after);
  }
  // MERGED, not replaced: snapping a vertex onto another outline's vertex
  // lands it on that exact coordinate, and the coordinate is the identity —
  // so from here they are ONE vertex and drag together, which is the whole
  // point of snapping to it. Overwriting would have quietly orphaned the
  // outline that was already there.
  const already = s.index.get(keyOf(pos)) ?? [];
  const seen = new Set(already.map((v) => `${v.t}:${v.r}:${v.c}:${v.i}`));
  const merged = [...already];
  for (const v of refs) {
    if (!seen.has(`${v.t}:${v.r}:${v.c}:${v.i}`)) merged.push({ ...v });
  }
  s.index.set(keyOf(pos), merged);
}

/** Replace a ring's points wholesale (a traced span), re-chunked, and
 *  re-address the model — a graft moves every vertex after it. */
export function setRing(s: Model, t: number, r: number, pts: Pt[]): void {
  const ring = s.terrs[t]!.rings[r]!;
  const chunks: Pt[][] = [];
  for (let i = 0; i < pts.length; i += CHUNK) chunks.push(pts.slice(i, i + CHUNK));
  ring.chunks = chunks;
  s.index = buildIndex(s.terrs);
}

/** Grow a territory's changed-envelope over a whole run of points. `touch`
 *  spreads its arguments, which a 90k-point ring would not survive. */
function touchAll(s: Model, t: number, pts: Pt[]): void {
  const terr = s.terrs[t]!;
  for (const p of pts) terr.env = extendEnv(terr.env, p);
}

/** Insert `pt` into the segment a→b on the outline at `v`, and into the
 *  same segment on every other outline that carries it (either direction).
 *  Returns every address the point now has. */
export function insertOnSegment(s: Model, v: VRef, pt: Pt): VRef[] {
  const ring = ringAt(s.terrs, v);
  const a = ptAt(s.terrs, v);
  const b = neighbours(ring, v).after;
  const targets: VRef[] = [v];
  const seenRings = new Set([`${v.t}/${v.r}`]);
  for (const ra of refsAt(s, a)) {
    const rk = `${ra.t}/${ra.r}`;
    if (seenRings.has(rk)) continue;
    const ring2 = ringAt(s.terrs, ra);
    const nb = neighbours(ring2, ra);
    if (keyOf(nb.after) === keyOf(b)) { targets.push(ra); seenRings.add(rk); continue; }
    if (keyOf(nb.before) === keyOf(b)) {
      const rb = refsAt(s, b).find((x) => x.t === ra.t && x.r === ra.r);
      if (rb) { targets.push(rb); seenRings.add(rk); }
    }
  }
  const out: VRef[] = [];
  for (const tv of targets) {
    const nv = insertAfter(ringAt(s.terrs, tv), tv, [pt[0], pt[1]]);
    reindexChunk(s, nv.t, nv.r, nv.c);
    touch(s, nv.t, pt, a, b);
    out.push(nv);
  }
  return out;
}

/** Remove one vertex from every outline carrying it. Refuses whole when any
 *  ring would drop below three corners. Returns the point, or null. */
export function deleteVertexRefs(s: Model, refs: VRef[]): Pt | null {
  if (!refs.length) return null;
  if (refs.some((v) => ringSize(ringAt(s.terrs, v)) <= 3)) return null;
  const p = ptAt(s.terrs, refs[0]!);
  s.index.delete(keyOf(p));
  // Highest index first within a chunk, so earlier splices don't shift later ones.
  const order = [...refs].sort((x, y) => x.t - y.t || x.r - y.r || x.c - y.c || y.i - x.i);
  for (const v of order) {
    const ring = ringAt(s.terrs, v);
    const { before, after } = neighbours(ring, v);
    ring.chunks[v.c]!.splice(v.i, 1);
    touch(s, v.t, p, before, after);
  }
  for (const v of order) reindexChunk(s, v.t, v.r, v.c);
  return p;
}

/** Put a deleted vertex back at every address it had. */
export function restoreVertexRefs(s: Model, refs: VRef[], p: Pt): void {
  const order = [...refs].sort((x, y) => x.t - y.t || x.r - y.r || x.c - y.c || x.i - y.i);
  for (const v of order) ringAt(s.terrs, v).chunks[v.c]!.splice(v.i, 0, [p[0], p[1]]);
  for (const v of order) {
    reindexChunk(s, v.t, v.r, v.c);
    touch(s, v.t, p);
  }
}

/** A ring's chunks as they are now — shallow copies, which is a true snapshot
 *  because an edit replaces a point or splices a chunk, never mutates a point. */
export function snapshotRing(s: Model, t: number, r: number): RingSnapshot {
  return { t, r, chunks: s.terrs[t]!.rings[r]!.chunks.map((ch) => ch.slice()) };
}

/** Put rings back exactly as snapshotted, and re-address the model once. */
export function restoreRings(s: Model, rings: RingSnapshot[]): void {
  for (const x of rings) s.terrs[x.t]!.rings[x.r]!.chunks = x.chunks.map((ch) => ch.slice());
  s.index = buildIndex(s.terrs);
}

// --- the push brush ---------------------------------------------------------------
//
// A brush dragged ACROSS a boundary moves it, the way a sculpting tool moves a
// surface: every vertex under the brush travels with it — all the way at the
// centre, not at all at the rim (snap.falloff) — so a stretch of front bends
// rather than one corner jumping. It is topological for free, because a vertex
// IS its coordinate: moving it moves every outline that carries it, so the two
// sides of a shared line bend together and never gap. And it densifies first:
// a long straight segment crossing the brush (a desert border, a CGAZ edge two
// kilometres long) gains vertices inside it, or the push would have nothing to
// bend but the segment's two far-away ends.

/** Ground ↔ screen, the two calls the brush needs from a map. */
export interface Projector {
  project(p: Pt): ScreenPt;
  unproject(q: ScreenPt): Pt;
}

export interface PushOpts {
  /** Segments crossing the brush longer than this (px) gain vertices first.
   *  Default: a fifth of the radius. */
  spacingPx?: number;
  /** Where a pushed vertex lands instead — a snap — or null to land where pushed. */
  snap?: (q: ScreenPt) => Pt | null;
  /** Called for a ring BEFORE the push first changes it: the undo snapshot. */
  onRing?: (t: number, r: number) => void;
}

type Box = [number, number, number, number];

function brushBox(proj: Projector, c: ScreenPt, r: number): Box {
  const pts = [proj.unproject({ x: c.x - r, y: c.y - r }), proj.unproject({ x: c.x + r, y: c.y - r }),
               proj.unproject({ x: c.x - r, y: c.y + r }), proj.unproject({ x: c.x + r, y: c.y + r })];
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

const segInBox = (a: Pt, b: Pt, box: Box): boolean =>
  !(Math.max(a[0], b[0]) < box[0] || Math.min(a[0], b[0]) > box[2]
    || Math.max(a[1], b[1]) < box[1] || Math.min(a[1], b[1]) > box[3]);

/** Every segment of every ring whose bounding box meets `box`, the closing
 *  edges included. Ground compares only — a country's worth of vertices is a
 *  pass of float tests per frame, and only what survives is projected. */
function eachSegment(terrs: Terr[], box: Box, visit: (a: Pt, b: Pt) => void): void {
  for (const terr of terrs) {
    for (const ring of terr.rings) {
      let first: Pt | null = null;
      let prev: Pt | null = null;
      for (const ch of ring.chunks) {
        for (const p of ch) {
          if (prev === null) first = p;
          else if (segInBox(prev, p, box)) visit(prev, p);
          prev = p;
        }
      }
      if (prev && first && prev !== first && segInBox(prev, first, box)) visit(prev, first);
    }
  }
}

/** Where along A→B (t in [0, 1]) the segment is inside the circle, or null. */
export function circleSpan(A: ScreenPt, B: ScreenPt, c: ScreenPt, r: number): [number, number] | null {
  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const fx = A.x - c.x;
  const fy = A.y - c.y;
  const a = dx * dx + dy * dy;
  if (a === 0) return null;
  const b = 2 * (fx * dx + fy * dy);
  const k = fx * fx + fy * fy - r * r;
  const disc = b * b - 4 * a * k;
  if (disc <= 0) return null;
  const sq = Math.sqrt(disc);
  const t0 = (-b - sq) / (2 * a);
  const t1 = (-b + sq) / (2 * a);
  if (t1 < 0 || t0 > 1) return null;
  return [Math.max(0, t0), Math.min(1, t1)];
}

/** The segment a→b on some ring: the address of whichever end runs forward
 *  to the other. */
function segmentRef(s: Model, a: Pt, b: Pt): { v: VRef; forward: boolean } | null {
  const kb = keyOf(b);
  for (const v of refsAt(s, a)) {
    if (keyOf(neighbours(ringAt(s.terrs, v), v).after) === kb) return { v, forward: true };
  }
  const ka = keyOf(a);
  for (const v of refsAt(s, b)) {
    if (keyOf(neighbours(ringAt(s.terrs, v), v).after) === ka) return { v, forward: false };
  }
  return null;
}

/** One frame of the push brush centred at `centre` (screen px) moving by
 *  `delta`. Densifies the segments crossing it, then moves every vertex under
 *  it by delta × falloff. Returns the addresses to repaint. */
export function pushAt(s: Model, proj: Projector, centre: ScreenPt, delta: ScreenPt,
                       radiusPx: number, opts: PushOpts = {}):
{ refs: VRef[]; moved: number; inserted: number } {
  const r = radiusPx;
  const spacing = Math.max(1, opts.spacingPx ?? r / 5);
  const mark = (refs: VRef[]): void => { for (const v of refs) opts.onRing?.(v.t, v.r); };

  // Densify — collected first and applied after, so the walk never meets a
  // ring it is in the middle of changing.
  const splits = new Map<string, { a: Pt; b: Pt; pts: Pt[] }>();
  eachSegment(s.terrs, brushBox(proj, centre, r + spacing), (a, b) => {
    const A = proj.project(a);
    const B = proj.project(b);
    const span = circleSpan(A, B, centre, r);
    if (!span) return;
    const len = Math.hypot(B.x - A.x, B.y - A.y);
    if (len <= spacing) return;
    const ka = keyOf(a);
    const kb = keyOf(b);
    const id = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
    if (splits.has(id)) return;             // the same segment, on the outline across
    const step = spacing / len;
    const pts: Pt[] = [];
    // From one step before the brush to one after it, on a grid measured
    // from `a`: the bend has room to start, and never a point on top of an end.
    for (let k = Math.max(1, Math.floor(span[0] / step)); k * step < 1 - step / 2; k++) {
      const t = k * step;
      if (t > span[1] + step) break;
      pts.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
    if (pts.length) splits.set(id, { a, b, pts });
  });
  let inserted = 0;
  const touched = new Set<string>();
  for (const { a, b, pts } of splits.values()) {
    const seg = segmentRef(s, a, b);
    if (!seg) continue;
    mark(refsAt(s, a));
    mark(refsAt(s, b));
    const ordered = seg.forward ? pts : [...pts].reverse();
    // Last first, each straight after the segment's start: they end in order,
    // and insertOnSegment puts each on every outline sharing the segment.
    for (let k = ordered.length - 1; k >= 0; k--) {
      insertOnSegment(s, seg.v, ordered[k]!);
      touched.add(keyOf(ordered[k]!));
      inserted++;
    }
  }

  // Move what is under the brush — each shared vertex once, by its coordinate.
  const box = brushBox(proj, centre, r);
  const under: [Pt, number][] = [];
  const seen = new Set<string>();
  for (const terr of s.terrs) {
    for (const ring of terr.rings) {
      for (const ch of ring.chunks) {
        for (const p of ch) {
          if (p[0] < box[0] || p[0] > box[2] || p[1] < box[1] || p[1] > box[3]) continue;
          const k = keyOf(p);
          if (seen.has(k)) continue;
          seen.add(k);
          const q = proj.project(p);
          const d = Math.hypot(q.x - centre.x, q.y - centre.y);
          if (d < r) under.push([p, d]);
        }
      }
    }
  }
  let moved = 0;
  for (const [p, d] of under) {
    const w = falloff(d, r);
    if (w <= 0) continue;
    const refs = refsAt(s, p);
    if (!refs.length) continue;
    const q = proj.project(p);
    const to = { x: q.x + delta.x * w, y: q.y + delta.y * w };
    const pos = opts.snap?.(to) ?? proj.unproject(to);
    if (pos[0] === p[0] && pos[1] === p[1]) continue;
    mark(refs);
    moveVertex(s, [...refs], p, pos);
    touched.add(keyOf(pos));
    moved++;
  }
  const refs: VRef[] = [];
  for (const k of touched) refs.push(...(s.index.get(k) ?? []));
  return { refs, moved, inserted };
}

// --- layers -----------------------------------------------------------------

export function installTerritoryEditor(map: maplibregl.Map): void {
  if (installed) return;
  installed = true;
  for (const id of [SRC_SHAPE, SRC_VERTS, SRC_DRAG, SRC_CUT, SRC_PIECES, SRC_GUIDE]) {
    map.addSource(id, { type: 'geojson', data: EMPTY });
  }
  // The cut preview sits under the outline stack: pieces are ground.
  map.addLayer({
    id: L_PIECE_FILL, type: 'fill', source: SRC_PIECES,
    paint: { 'fill-color': ['case', ['==', ['get', 'picked'], 1], '#ff8f00', '#ffd166'],
             'fill-opacity': ['case', ['==', ['get', 'picked'], 1], 0.45, 0.2] },
  });
  map.addLayer({
    id: L_PIECE_LINE, type: 'line', source: SRC_PIECES,
    paint: { 'line-color': '#ff8f00', 'line-width': 2, 'line-dasharray': [2, 1.5] },
  });
  // Admin linework and roads (guides.ts) sit under the country outline and
  // everything else the editor draws: the faintest of the guides.
  installGuideLayers(map);
  // The country outline, under everything the editor draws: what a dragged
  // vertex snaps to and what a trace can follow. Visible only while editing,
  // and faint — it is a guide, not a layer the operator turned on.
  map.addLayer({
    id: L_GUIDE, type: 'line', source: SRC_GUIDE,
    layout: { 'line-join': 'round', 'line-cap': 'round', visibility: 'none' },
    paint: { 'line-color': '#7fd1ff', 'line-width': 1.2, 'line-dasharray': [1, 2],
             'line-opacity': 0.65 },
  });
  map.addLayer({
    id: L_CASING, type: 'line', source: SRC_SHAPE,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#12202c', 'line-width': 5, 'line-opacity': 0.75 },
  });
  map.addLayer({
    id: L_LINE, type: 'line', source: SRC_SHAPE,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#ffd166', 'line-width': 2.2 },
  });
  map.addLayer({
    id: L_VERTS, type: 'circle', source: SRC_VERTS, minzoom: HANDLE_MINZOOM,
    paint: {
      'circle-radius': 5, 'circle-color': '#ffffff',
      'circle-stroke-color': '#ff8f00', 'circle-stroke-width': 2,
    },
  });
  map.addLayer({
    id: L_DRAG_LINE, type: 'line', source: SRC_DRAG,
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#ff8f00', 'line-width': 2.6 },
  });
  map.addLayer({
    id: L_DRAG_VERT, type: 'circle', source: SRC_DRAG,
    filter: ['all', ['==', ['geometry-type'], 'Point'], ['!', ['has', 'sel']]],
    paint: {
      'circle-radius': 7, 'circle-color': '#ff8f00',
      'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2,
    },
  });
  map.addLayer({
    id: L_SEL_VERT, type: 'circle', source: SRC_DRAG,
    filter: ['has', 'sel'],
    paint: {
      'circle-radius': 8, 'circle-color': '#ff4d4d',
      'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2.5,
    },
  });
  // Where a snap has taken hold, or where a trace started: a ring around the
  // point, so the jump is something the operator sees happen.
  map.addLayer({
    id: L_SNAP, type: 'circle', source: SRC_DRAG,
    filter: ['==', ['get', 'snap'], 1],
    paint: {
      'circle-radius': 10, 'circle-color': '#7fd1ff', 'circle-opacity': 0.2,
      'circle-stroke-color': '#7fd1ff', 'circle-stroke-width': 2,
    },
  });
  map.addLayer({
    id: L_CUT_LINE, type: 'line', source: SRC_CUT,
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#ff4d4d', 'line-width': 3 },
  });
  map.addLayer({
    id: L_CUT_VERTS, type: 'circle', source: SRC_CUT,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-radius': 5.5,
      // The snap target under the cursor is amber; placed corners are white.
      'circle-color': ['case', ['==', ['get', 'role'], 'snap'], '#ffd166', '#ffffff'],
      'circle-stroke-color': '#ff4d4d', 'circle-stroke-width': 2.5,
    },
  });
  map.addLayer({
    id: L_PIECE_LABEL, type: 'symbol', source: SRC_PIECES,
    filter: ['==', ['geometry-type'], 'Point'],
    layout: { 'text-field': ['get', 'label'], 'text-size': 13, 'text-allow-overlap': true },
    paint: { 'text-color': '#12202c', 'text-halo-color': '#ffd166', 'text-halo-width': 2 },
  });

  const canvas = map.getCanvas();
  const pointAt = (ev: PointerEvent): [number, number] => {
    const rect = canvas.getBoundingClientRect();
    return [ev.clientX - rect.left, ev.clientY - rect.top];
  };
  const vertexAt = (x: number, y: number, pad: number): VRef | null => {
    const box: [maplibregl.PointLike, maplibregl.PointLike] =
      [[x - pad, y - pad], [x + pad, y + pad]];
    const hit = map.queryRenderedFeatures(box, { layers: [L_VERTS] })[0];
    if (!hit) return null;
    return { t: Number(hit.properties.t), r: Number(hit.properties.r),
             c: Number(hit.properties.c), i: Number(hit.properties.i) };
  };

  canvas.addEventListener('pointerdown', (ev: PointerEvent) => {
    if (!state || state.drag || state.saving) return;
    if (ev.button === 2) return;   // the contextmenu handler deletes
    const s = state;
    const [x, y] = pointAt(ev);
    if (s.tracing) {
      // Tracing aims at a line rather than grabbing a vertex, so it takes the
      // click whole — including a click that happens to land on a handle.
      ev.preventDefault();
      ev.stopPropagation();
      traceClick(map, s, x, y);
      return;
    }
    if (s.pushing) {
      // The brush takes the press whole, handle or no handle: it moves every
      // vertex under it, and the map must not pan underneath.
      ev.preventDefault();
      ev.stopPropagation();
      startPush(map, s, ev, [x, y]);
      return;
    }
    const touchy = ev.pointerType !== 'mouse';
    const pad = touchy ? 14 : 5;
    let refs: VRef[];
    let inserted = false;
    const hit = vertexAt(x, y, pad);
    if (hit) {
      refs = refsAt(s, ptAt(s.terrs, hit));
      if (!refs.length) refs = [hit];
    } else {
      // Grab the line itself: a new vertex is born on the nearest segment,
      // where the pointer is — on every outline sharing that segment — and
      // the drag moves it. A tap adds it and leaves it there.
      const box: [maplibregl.PointLike, maplibregl.PointLike] =
        [[x - pad, y - pad], [x + pad, y + pad]];
      const onLine = map.queryRenderedFeatures(box, { layers: [L_LINE] })[0];
      if (!onLine) { select(map, null); return; }
      const t = Number(onLine.properties.t);
      const r = Number(onLine.properties.r);
      const seg = nearestSegment(map, s.terrs[t]!.rings[r]!, [x, y]);
      if (!seg) return;
      const ll = map.unproject([x, y]);
      refs = insertOnSegment(s, { t, r, c: seg.c, i: seg.i }, [ll.lng, ll.lat]);
      inserted = true;
      paintChunks(map, refs);   // the vertex exists from the first frame
    }
    ev.preventDefault();
    ev.stopPropagation();
    select(map, null);
    canvas.setPointerCapture(ev.pointerId);
    map.dragPan.disable();
    canvas.style.cursor = 'grabbing';
    const start = ptAt(s.terrs, refs[0]!);
    s.drag = { refs, pos: [start[0], start[1]], prev: [start[0], start[1]], inserted,
               pointerId: ev.pointerId, start: [x, y],
               tapPx: touchy ? TAP_PX.touch : TAP_PX.mouse, snapped: null };
    // The lines to snap against, indexed once for this drag — the camera
    // cannot move while a vertex is in hand.
    if (s.snapping) ensureGuides(map);
    // A vertex must not snap to where it already is, nor to the two segments
    // that move with it.
    const held = keyOf(start);
    const rejectSelf = (hit: SnapHit): boolean => {
      const g = state?.guides?.[hit.guide];
      if (!g) return false;
      if (hit.kind === 'vertex') return keyOf(hit.pos as Pt) === held;
      const a = g.pts[hit.seg % g.pts.length] as Pt;
      const b = g.pts[(hit.seg + 1) % g.pts.length] as Pt;
      return keyOf(a) === held || keyOf(b) === held;
    };
    paintDrag(map);

    const onMove = (move: PointerEvent): void => {
      if (!state?.drag || move.pointerId !== ev.pointerId) return;
      const at = pointAt(move);
      const ll = map.unproject(at);
      const was = state.drag.snapped?.kind;
      // ALT places the vertex exactly where the hand is: snapping is a help,
      // never a cage, and the one thing you cannot do by aiming better is get
      // NEAR a line without touching it.
      const hit = state.snapping && !move.altKey && state.snapIndex
        ? nearestSnap(state.snapIndex, { x: at[0]!, y: at[1]! }, SNAP_PX, rejectSelf)
        : null;
      state.drag.snapped = hit;
      // A snapped vertex takes the guide's OWN coordinate, not a round trip
      // through the screen — identical is what makes the two outlines share
      // the vertex from here on (see moveVertex).
      state.drag.pos = hit ? [hit.pos[0]!, hit.pos[1]!] : [ll.lng, ll.lat];
      paintDrag(map);
      if (!!hit !== !!was || hit?.kind !== was) {
        state.bar.update(state.steps.length, hit ? snapLabel(hit) : undefined);
      }
    };
    const onUp = (up: PointerEvent): void => {
      if (up.pointerId !== ev.pointerId) return;
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      map.dragPan.enable();
      canvas.style.cursor = '';
      const s2 = state;
      if (!s2?.drag) return;
      const d = s2.drag;
      s2.drag = null;
      (map.getSource(SRC_DRAG) as maplibregl.GeoJSONSource).setData(EMPTY);
      const [ux, uy] = pointAt(up);
      const tapped = Math.hypot(ux - d.start[0], uy - d.start[1]) < d.tapPx;
      if (tapped && !d.inserted) {
        // A tap on an existing vertex selects it for deletion; nothing moved.
        select(map, d.refs[0]!);
        return;
      }
      if (d.inserted) {
        if (!tapped) moveVertex(s2, d.refs, d.prev, d.pos);
        s2.steps.push({ kind: 'insert', refs: d.refs });
      } else {
        moveVertex(s2, d.refs, d.prev, d.pos);
        s2.steps.push({ kind: 'move', refs: d.refs, prev: d.prev, pos: d.pos });
      }
      paintChunks(map, d.refs);
      paintHandles(map);
      if (s2.guidesStale) { s2.guidesStale = false; invalidateGuides(); }
      s2.bar.update(s2.steps.length);
    };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
  });

  // Right-click deletes the vertex under the pointer outright.
  canvas.addEventListener('contextmenu', (ev: MouseEvent) => {
    if (!state || state.drag || state.saving) return;
    const rect = canvas.getBoundingClientRect();
    const at = vertexAt(ev.clientX - rect.left, ev.clientY - rect.top, 5);
    if (!at) return;
    ev.preventDefault();
    ev.stopPropagation();
    deleteVertex(map, at);
  });

  // The cursor says what a press would do: grab a vertex, or bend the line.
  map.on('mousemove', (e) => {
    if (!state || state.drag) return;
    if (state.pushing) {
      canvas.style.cursor = 'crosshair';
      placeBrush(map, [e.point.x, e.point.y]);
      return;
    }
    const p = e.point;
    const box: [maplibregl.PointLike, maplibregl.PointLike] =
      [[p.x - 5, p.y - 5], [p.x + 5, p.y + 5]];
    // One query for both layers — this runs on every mouse move while editing.
    const hits = map.queryRenderedFeatures(box, { layers: [L_VERTS, L_LINE] });
    if (hits.some((f) => f.layer.id === L_VERTS)) {
      canvas.style.cursor = 'grab';
    } else if (hits.length) {
      canvas.style.cursor = 'copy';
    } else {
      canvas.style.cursor = '';
    }
  });
  // Handles are cut to the view; the chip follows its vertex.
  map.on('moveend', () => {
    if (!state) return;
    paintHandles(map);
    invalidateGuides();   // the guides were projected for the old camera
  });
  map.on('move', () => { if (state?.selected) placeChip(map); });
  map.on('mouseout', () => hideBrush());
}

// --- snapping and tracing ---------------------------------------------------

/** The country's ADM0 rings, for this edit. Best-effort: without them
 *  snapping still works against the other territories, which is where most of
 *  the shared line is anyway. */
async function loadOutline(map: maplibregl.Map, iso: string): Promise<void> {
  countryRings = [];
  if (!iso) return;
  try {
    const dto = await get<{ geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon }>(
      `/api/boundaries/outline?iso=${encodeURIComponent(iso)}`);
    const polys: Pt[][][] = dto.geometry.type === 'Polygon'
      ? [dto.geometry.coordinates as Pt[][]]
      : dto.geometry.coordinates as Pt[][][];
    for (const poly of polys) {
      for (const ring of poly) {
        const pts = ring.map((c): Pt => [c[0]!, c[1]!]);
        const first = pts[0];
        const last = pts[pts.length - 1];
        if (pts.length > 1 && first && last && keyOf(first) === keyOf(last)) pts.pop();
        if (pts.length >= 3) countryRings.push(pts);
      }
    }
  } catch {
    /* no outline: the territory outlines are still guides */
  }
  if (!state) return;
  const src = map.getSource(SRC_GUIDE) as maplibregl.GeoJSONSource | undefined;
  src?.setData({
    type: 'FeatureCollection',
    features: countryRings.map((pts) => ({
      type: 'Feature', properties: {},
      geometry: { type: 'LineString', coordinates: [...pts, pts[0]!] },
    })),
  });
  if (map.getLayer(L_GUIDE)) map.setLayoutProperty(L_GUIDE, 'visibility', 'visible');
  state.guides = null;      // the new lines belong in the index
}

/** Build (or reuse) the lines this camera can snap to. Screen coordinates go
 *  stale the moment the map moves, so `moveend` drops them and the next drag
 *  pays for them again — the same bargain paintHandles already makes. */
function ensureGuides(map: maplibregl.Map): EditGuide[] {
  const s = state;
  if (!s) return [];
  if (s.guides) return s.guides;
  const guides: EditGuide[] = [];
  let total = 0;
  const project = (p: Pt): { x: number; y: number } => {
    const q = map.project(p as [number, number]);
    return { x: q.x, y: q.y };
  };
  const add = (pts: Pt[], source: 'territory' | 'boundary',
               terr?: { t: number; r: number }): void => {
    if (pts.length < 2 || total + pts.length > SNAP_MAX_PTS) return;
    total += pts.length;
    guides.push({ pts, screen: pts.map(project), closed: true, source, terr });
  };
  // The view's admin linework and roads first: they are small (cut to the
  // view), and they are what a front is most often moved onto, so a country's
  // worth of outline must not crowd them out of the budget.
  for (const line of s.lines?.lines() ?? []) {
    if (line.pts.length < 2 || total + line.pts.length > SNAP_MAX_PTS) continue;
    total += line.pts.length;
    guides.push({ pts: line.pts, screen: line.pts.map(project), closed: false, source: line.source });
  }
  s.terrs.forEach((t, ti) => t.rings.forEach((ring, ri) =>
    add(flatRing(ring), 'territory', { t: ti, r: ri })));
  for (const ring of countryRings) add(ring, 'boundary');
  s.guides = guides;
  s.snapIndex = buildSnapIndex(guides);
  return guides;
}

function invalidateGuides(): void {
  if (!state) return;
  state.guides = null;
  state.snapIndex = null;
}

/** What a snap landed on, in words — the bar says it rather than leaving the
 *  operator to infer a jump. */
function snapLabel(hit: SnapHit): string {
  if (hit.source === 'road') {
    return hit.kind === 'vertex' ? 'snapped to a point on a road' : 'snapped to a road';
  }
  if (hit.source === 'admin') {
    const lv = state?.lines?.level();
    const what = lv ? `an ADM${lv} boundary` : 'an admin boundary';
    return hit.kind === 'vertex' ? `snapped to a corner of ${what}` : `snapped to ${what}`;
  }
  const what = hit.source === 'boundary' ? 'the country outline' : 'a neighbour';
  return hit.kind === 'vertex' ? `snapped to ${what}'s corner` : `snapped to ${what}'s edge`;
}

function paintSnapMark(map: maplibregl.Map, hit: SnapHit | null): void {
  const src = map.getSource(SRC_DRAG) as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  src.setData(hit ? {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', properties: { snap: 1 },
                 geometry: { type: 'Point', coordinates: hit.pos } }],
  } : EMPTY);
}

function setSnapping(map: maplibregl.Map, on: boolean): void {
  if (!state) return;
  state.snapping = on;
  state.bar.setExtra('snap', { on });
  state.bar.update(state.steps.length,
                   on ? 'Snapping on — hold Alt to place a vertex freely'
                      : 'Snapping off');
  if (!on && state.drag) paintDrag(map);
}

function setTracing(map: maplibregl.Map, on: boolean): void {
  const s = state;
  if (!s) return;
  if (on && s.pushing) setPushing(map, false);
  s.tracing = on;
  s.traceFrom = null;
  s.bar.setExtra('trace', { on });
  s.bar.setExtra('vertices', { on: !on && !s.pushing });
  s.bar.setHint(on ? 'Click a start and end point on connected guide lines. The existing border follows the route between them.' : 'Drag corners to reshape the border. Click an edge to add a corner. Save boundary applies your changes.');
  paintSnapMark(map, null);
  map.getCanvas().style.cursor = on ? 'crosshair' : '';
  if (on) ensureGuides(map);
  s.bar.update(s.steps.length, on
    ? 'Click where the new line starts — on an outline, an admin boundary or a road'
    : undefined);
}

/** A trace click: the first names where the run starts, the second where it
 *  ends, and the run between them becomes the boundary. */
function traceClick(map: maplibregl.Map, s: State, x: number, y: number): void {
  ensureGuides(map);
  const hit = s.snapIndex ? nearestSnap(s.snapIndex, { x, y }, TRACE_PX) : null;
  if (!hit) {
    toast('Click on a line to trace — an outline, an admin boundary or a road.');
    return;
  }
  if (!s.traceFrom) {
    s.traceFrom = hit;
    paintSnapMark(map, hit);
    s.bar.update(s.steps.length, 'Now click where the run ends — on that line, or one joined to it');
    return;
  }
  const guides = s.guides ?? [];
  const from = s.traceFrom;
  // Both ends on one line: the walk along it. On two: the shortest way along
  // the lines that join them — every admin edge and road in view, but no
  // territory outline besides the ends' own (a country's worth of vertices,
  // and the run would wander along the very outline it is traced FOR).
  const run = traceNetwork(guides, from, hit, (g) => g.source !== 'territory');
  s.traceFrom = null;
  paintSnapMark(map, null);
  if (!run || run.length < 2) {
    toast('Those two points are not joined along the lines in view — pick ends on one line, or on lines that meet.');
    return;
  }
  // A run along ONE outline is not traced onto that outline — it already is
  // that line. A run from an outline to a road is traced onto the outline.
  const own = from.guide === hit.guide ? (guides[hit.guide] as EditGuide | undefined)?.terr : undefined;
  applyTrace(map, s, own ? [own] : [], run);
}

/** Graft a traced run into whichever outline it was traced FOR: the nearest
 *  one that is not a line it was traced along. One outline only — a run
 *  pulled onto a neighbour's line leaves that neighbour alone, and the server
 *  resolves the overlap exactly as it does for a one-sided drag. */
function applyTrace(map: maplibregl.Map, s: State, skip: { t: number; r: number }[], run: Pt[]): void {
  const head = run[0]!;
  const tail = run[run.length - 1]!;
  const gap = (a: Pt, b: Pt): number => Math.hypot(a[0] - b[0], a[1] - b[1]);
  let best: { t: number; r: number; pts: Pt[]; ia: number; ib: number; d: number } | null = null;
  s.terrs.forEach((terr, t) => terr.rings.forEach((ring, r) => {
    if (skip.some((k) => k.t === t && k.r === r)) return;
    const pts = flatRing(ring);
    if (pts.length < 3) return;
    const ia = nearestVertex(pts, head);
    const ib = nearestVertex(pts, tail);
    const d = gap(pts[ia]!, head) + gap(pts[ib]!, tail);
    if (!best || d < best.d) best = { t, r, pts, ia, ib, d };
  }));
  const pick = best as { t: number; r: number; pts: Pt[]; ia: number; ib: number } | null;
  if (!pick) { toast('No outline near that line to trace onto.'); return; }
  if (pick.ia === pick.ib) {
    toast('Both ends land on the same corner — trace a longer run.');
    return;
  }
  const side = chooseSide(pick.pts, pick.ia, pick.ib, run);
  const next = graftRun(pick.pts, pick.ia, pick.ib, run, side);
  if (!next) { toast('That would not leave an outline behind.'); return; }
  s.steps.push({ kind: 'rings', rings: [snapshotRing(s, pick.t, pick.r)] });
  setRing(s, pick.t, pick.r, next);
  // Old and new both: the server diffs inside this box, and the span that was
  // swallowed is as much a change as the line that replaced it.
  touchAll(s, pick.t, pick.pts);
  touchAll(s, pick.t, next);
  invalidateGuides();
  paintShape(map);
  paintHandles(map);
  s.bar.update(s.steps.length,
               `traced ${run.length} points onto the boundary`);
}

function chunkId(t: number, r: number, c: number): number {
  return (t * 1000 + r) * 100_000 + c;
}

function chunkFeature(terrs: Terr[], t: number, r: number, c: number): GeoJSON.Feature | null {
  const line = chunkLine(terrs[t]!.rings[r]!, c);
  if (!line) return null;
  return { type: 'Feature', id: chunkId(t, r, c), properties: { t, r, c },
           geometry: { type: 'LineString', coordinates: line } };
}

/** Every outline, once, at the start of an edit. */
function paintShape(map: maplibregl.Map): void {
  const shape = map.getSource(SRC_SHAPE) as maplibregl.GeoJSONSource | undefined;
  if (!shape) return;
  if (!state) { shape.setData(EMPTY); return; }
  const lines: GeoJSON.Feature[] = [];
  state.terrs.forEach((terr, t) => terr.rings.forEach((ring, r) => ring.chunks.forEach((_, c) => {
    const f = chunkFeature(state!.terrs, t, r, c);
    if (f) lines.push(f);
  })));
  shape.setData({ type: 'FeatureCollection', features: lines });
}

/** Re-send only the chunks an edit touched and the ones whose line ends at
 *  their first vertex — remove-then-add, so an emptied chunk disappears and
 *  an undo brings it back. */
function paintChunks(map: maplibregl.Map, refs: VRef[]): void {
  const shape = map.getSource(SRC_SHAPE) as maplibregl.GeoJSONSource | undefined;
  if (!shape || !state) return;
  const touched = new Map<number, [number, number, number]>();
  for (const v of refs) {
    const ring = ringAt(state.terrs, v);
    for (const c of [v.c, prevChunk(ring, v.c)]) touched.set(chunkId(v.t, v.r, c), [v.t, v.r, c]);
  }
  const remove: number[] = [];
  const add: GeoJSON.Feature[] = [];
  for (const [id, [t, r, c]] of touched) {
    remove.push(id);
    const f = chunkFeature(state.terrs, t, r, c);
    if (f) add.push(f);
  }
  shape.updateData({ remove, add });
}

/** The handles in view — one per shared vertex — or none, with the count,
 *  when there are too many to be draggable. A pass over the coordinates. */
function paintHandles(map: maplibregl.Map): void {
  const verts = map.getSource(SRC_VERTS) as maplibregl.GeoJSONSource | undefined;
  if (!verts) return;
  if (!state) { verts.setData(EMPTY); return; }
  if (map.getZoom() < HANDLE_MINZOOM) {
    state.crowded = 0;
    verts.setData(EMPTY);
    hintCrowd(map);
    return;
  }
  const b = map.getBounds();
  const pw = (b.getEast() - b.getWest()) * 0.25;
  const ph = (b.getNorth() - b.getSouth()) * 0.25;
  const w = b.getWest() - pw;
  const e = b.getEast() + pw;
  const s = b.getSouth() - ph;
  const n = b.getNorth() + ph;
  const points: GeoJSON.Feature[] = [];
  const seen = new Set<string>();
  let count = 0;
  state.terrs.forEach((terr, t) => terr.rings.forEach((ring, r) => ring.chunks.forEach((ch, c) =>
    ch.forEach((p, i) => {
      if (p[0] < w || p[0] > e || p[1] < s || p[1] > n) return;
      const k = keyOf(p);
      if (seen.has(k)) return;
      seen.add(k);
      count++;
      if (count <= HANDLES_MAX) {
        points.push({ type: 'Feature', properties: { t, r, c, i },
          geometry: { type: 'Point', coordinates: p } });
      }
    }))));
  state.crowded = count > HANDLES_MAX ? count : 0;
  verts.setData(state.crowded ? EMPTY : { type: 'FeatureCollection', features: points });
  hintCrowd(map);
}

function hintCrowd(map: maplibregl.Map): void {
  if (!state) return;
  if (map.getZoom() < HANDLE_MINZOOM) {
    state.bar.update(state.steps.length, state.steps.length
      ? `${state.steps.length} edit${state.steps.length === 1 ? '' : 's'} · zoom in for the vertices`
      : 'Zoom in for the vertices, or grab the line to bend it');
  } else if (state.crowded) {
    state.bar.update(state.steps.length,
      `${state.crowded.toLocaleString()} vertices in view — zoom in to drag them`);
  } else {
    state.bar.update(state.steps.length);
  }
}

function paintDrag(map: maplibregl.Map): void {
  const src = map.getSource(SRC_DRAG) as maplibregl.GeoJSONSource | undefined;
  if (!src || !state?.drag) return;
  const { refs, pos } = state.drag;
  const features: GeoJSON.Feature[] = [];
  for (const v of refs) {
    const { before, after } = neighbours(ringAt(state.terrs, v), v);
    features.push({ type: 'Feature', properties: {},
      geometry: { type: 'LineString', coordinates: [before, pos, after] } });
  }
  features.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: pos } });
  if (state.drag.snapped) {
    features.push({ type: 'Feature', properties: { snap: 1 },
                    geometry: { type: 'Point', coordinates: pos } });
  }
  src.setData({ type: 'FeatureCollection', features });
}

// --- the push brush, on the map --------------------------------------------------

function projectorOf(map: maplibregl.Map): Projector {
  return {
    project: (p) => {
      const q = map.project(p as [number, number]);
      return { x: q.x, y: q.y };
    },
    unproject: (q) => {
      const ll = map.unproject([q.x, q.y]);
      return [ll.lng, ll.lat];
    },
  };
}

function canvasPoint(canvas: HTMLCanvasElement, ev: PointerEvent): [number, number] {
  const rect = canvas.getBoundingClientRect();
  return [ev.clientX - rect.left, ev.clientY - rect.top];
}

function setPushing(map: maplibregl.Map, on: boolean): void {
  const s = state;
  if (!s) return;
  if (on && s.tracing) setTracing(map, false);
  s.pushing = on;
  s.bar.setExtra('push', { on });
  s.bar.setExtra('vertices', { on: !on && !s.tracing });
  s.bar.setExtra('radius', { hidden: !on });
  s.bar.setHint(on ? 'Drag the circular brush across an existing border to move it. Both sides of a shared border move together. Resize with [ and ]. Undo reverses a stroke.' : 'Drag corners to reshape the border. Click an edge to add a corner. Save boundary applies your changes.');
  select(map, null);
  if (!on) hideBrush();
  map.getCanvas().style.cursor = on ? 'crosshair' : '';
  s.bar.update(s.steps.length, on
    ? `Push: drag the brush across a line to move it · ${s.radiusPx}px, [ and ] resize`
    : undefined);
}

function stepRadius(map: maplibregl.Map, dir: 1 | -1, wrap: boolean): void {
  const s = state;
  if (!s) return;
  const n = BRUSH_RADII.length;
  const i = Math.max(0, BRUSH_RADII.indexOf(s.radiusPx));
  s.radiusPx = BRUSH_RADII[wrap ? (i + dir + n) % n : Math.max(0, Math.min(n - 1, i + dir))]!;
  s.bar.setExtra('radius', { label: `◌ ${s.radiusPx}px` });
  if (brushAt) placeBrush(map, brushAt);
  s.bar.update(s.steps.length, `brush ${s.radiusPx}px`);
}

/** The brush's reach, following the pointer: a DOM ring, because a map
 *  layer would repaint the map on every mouse move. */
let brushEl: HTMLDivElement | null = null;
let brushAt: [number, number] | null = null;

function placeBrush(map: maplibregl.Map, at: [number, number]): void {
  if (!state?.pushing) { hideBrush(); return; }
  if (!brushEl) {
    brushEl = document.createElement('div');
    brushEl.className = 'terr-brush';
    document.body.appendChild(brushEl);
  }
  brushAt = at;
  const rect = map.getCanvas().getBoundingClientRect();
  const r = state.radiusPx;
  brushEl.style.width = `${r * 2}px`;
  brushEl.style.height = `${r * 2}px`;
  brushEl.style.left = `${rect.left + at[0] - r}px`;
  brushEl.style.top = `${rect.top + at[1] - r}px`;
}

function hideBrush(): void {
  brushEl?.remove();
  brushEl = null;
  brushAt = null;
}

/** One stroke of the push brush: each animation frame pushes the line under
 *  the brush by however far the pointer moved (pushAt), repainting only the
 *  chunks it touched; the whole stroke is ONE undo step — the rings it
 *  changed, as they were before it began. */
function startPush(map: maplibregl.Map, s: State, ev: PointerEvent, at: [number, number]): void {
  const canvas = map.getCanvas();
  canvas.setPointerCapture(ev.pointerId);
  map.dragPan.disable();
  select(map, null);
  if (s.snapping) ensureGuides(map);
  const proj = projectorOf(map);
  const before = new Map<string, RingSnapshot>();
  let last: ScreenPt = { x: at[0], y: at[1] };
  let pending: ScreenPt | null = null;
  let alt = ev.altKey;
  let frame = 0;
  let changed = false;
  placeBrush(map, at);

  const step = (): void => {
    frame = 0;
    const st = state;
    if (!st || !pending) return;
    const to = pending;
    pending = null;
    const delta = { x: to.x - last.x, y: to.y - last.y };
    if (Math.hypot(delta.x, delta.y) < PUSH_MIN_PX) return;
    const index = st.snapping && !alt ? st.snapIndex : null;
    // Magnetic, not a cage: a pushed vertex that comes within SNAP_PX of an
    // admin boundary, a road or the coast clings to it, and pushing on past
    // pulls it off again. Edges only, so a run of vertices does not pile onto
    // one corner; and never a territory outline — those are the lines being
    // pushed, projected where they were when the stroke began.
    const snap = index
      ? (q: ScreenPt): Pt | null => {
          const hit = nearestSnap(index, q, SNAP_PX, (h) => h.source === 'territory',
                                  { vertices: false });
          return hit ? [hit.pos[0]!, hit.pos[1]!] : null;
        }
      : undefined;
    const res = pushAt(st, proj, last, delta, st.radiusPx, {
      snap,
      onRing: (t, r) => {
        const k = `${t}/${r}`;
        if (!before.has(k)) before.set(k, snapshotRing(st, t, r));
      },
    });
    last = to;
    if (res.moved || res.inserted) {
      changed = true;
      paintChunks(map, res.refs);
    }
  };
  const onMove = (m: PointerEvent): void => {
    if (m.pointerId !== ev.pointerId) return;
    alt = m.altKey;
    const p = canvasPoint(canvas, m);
    placeBrush(map, p);
    pending = { x: p[0], y: p[1] };
    if (!frame) frame = requestAnimationFrame(step);
  };
  const onUp = (up: PointerEvent): void => {
    if (up.pointerId !== ev.pointerId) return;
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onUp);
    if (frame) { cancelAnimationFrame(frame); step(); }
    map.dragPan.enable();
    const st = state;
    if (!st || !changed || !before.size) return;
    st.steps.push({ kind: 'rings', rings: [...before.values()] });
    invalidateGuides();     // the outlines the snapper projected have moved
    paintHandles(map);
    st.bar.update(st.steps.length,
                  before.size === 1 ? 'pushed the line' : `pushed ${before.size} outlines together`);
  };
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);
}

// --- selecting and deleting a vertex ------------------------------------------

function select(map: maplibregl.Map, at: VRef | null): void {
  if (!state) return;
  state.selected = at;
  const src = map.getSource(SRC_DRAG) as maplibregl.GeoJSONSource | undefined;
  if (!at) {
    src?.setData(EMPTY);
    chip?.remove();
    chip = null;
    return;
  }
  const p = ptAt(state.terrs, at);
  src?.setData({ type: 'FeatureCollection', features: [
    { type: 'Feature', properties: { sel: 1 }, geometry: { type: 'Point', coordinates: p } },
  ] });
  if (!chip) {
    chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'terr-vchip';
    chip.textContent = '✕ Delete vertex';
    chip.title = 'Remove this vertex (Delete). The selection moves on to the next one.';
    for (const type of ['pointerdown', 'mousedown', 'touchstart']) {
      chip.addEventListener(type, (ev) => ev.stopPropagation());
    }
    chip.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (state?.selected) deleteVertex(map, state.selected);
    });
    document.body.appendChild(chip);
  }
  placeChip(map);
}

function placeChip(map: maplibregl.Map): void {
  if (!chip || !state?.selected) return;
  const p = map.project(ptAt(state.terrs, state.selected));
  const rect = map.getCanvas().getBoundingClientRect();
  chip.style.left = `${rect.left + p.x + 12}px`;
  chip.style.top = `${rect.top + p.y - 14}px`;
}

/** Delete the vertex at `at` from every outline carrying it, and select the
 *  next vertex along the line so a run of deletes is a run of presses. */
function deleteVertex(map: maplibregl.Map, at: VRef): void {
  const s = state;
  if (!s || s.saving) return;
  const p = ptAt(s.terrs, at);
  const refs = refsAt(s, p);
  const all = refs.length ? refs : [at];
  const { after } = neighbours(ringAt(s.terrs, at), at);
  const removed = deleteVertexRefs(s, all);
  if (!removed) { toast('A ring needs at least three corners.'); return; }
  s.steps.push({ kind: 'delete', refs: all, prev: removed });
  paintChunks(map, all);
  paintHandles(map);
  s.bar.update(s.steps.length);
  select(map, refsAt(s, after)[0] ?? null);
}

function undo(map: maplibregl.Map): void {
  const s = state;
  if (!s || s.saving || s.drag) return;
  const step = s.steps.pop();
  if (!step) return;
  select(map, null);
  if (step.kind === 'rings') {
    // A graft or a push has no addresses to put back — the rings return whole.
    restoreRings(s, step.rings);
    invalidateGuides();
    paintShape(map);
    paintHandles(map);
    s.bar.update(s.steps.length);
    return;
  }
  if (step.kind === 'move') moveVertex(s, step.refs, step.pos, step.prev);
  else if (step.kind === 'insert') deleteVertexRefs(s, step.refs);
  else restoreVertexRefs(s, step.refs, step.prev);
  paintChunks(map, step.refs);
  paintHandles(map);
  s.bar.update(s.steps.length);
}

// --- the drag flow --------------------------------------------------------------

/** Every territory of `entity`'s country, the entity itself first. Fetched
 *  fresh rather than read from a pane: the edit must start from the current
 *  version of every outline it may hand back. */
async function loadCountry(entity: EntityDto): Promise<EntityDto[]> {
  const iso = String(entity.attrs.country_iso ?? '').toUpperCase();
  let peers: EntityDto[] = [];
  try {
    const res = await get<{ entities: EntityDto[] }>('/api/entities?types=territory&limit=500');
    peers = res.entities.filter((e) => e.id !== entity.id && e.geometry
      && iso && String(e.attrs.country_iso ?? '').toUpperCase() === iso);
  } catch {
    peers = [];
  }
  const me = peers.length ? await get<EntityDto>(`/api/entities/${entity.id}`).catch(() => entity) : entity;
  return [me, ...peers];
}

/** Start dragging the boundaries of `entity`'s country. One edit at a time —
 *  a second call cancels the first. */
export function editTerritoryBoundary(map: maplibregl.Map, entity: EntityDto,
                                      hooks: EditHooks): void {
  cancelTerritoryEdit();
  if (!map.getSource(SRC_SHAPE)) installTerritoryEditor(map);
  const releaseClicks = holdMapClicks();
  let cancelled = false;
  const loading = showDrawBar({ surface: map.getCanvas(), title: 'Loading boundaries',
    minPoints: 1, minHint: 'Loading the existing outlines…',
    onFinish: () => {}, onUndo: () => {},
    onCancel: () => { cancelTerritoryEdit(); hooks.onDone(); },
  });
  const onCancelKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') { cancelTerritoryEdit(); hooks.onDone(); }
  };
  document.addEventListener('keydown', onCancelKey);
  const releaseLoading = (): void => {
    loading.close(); releaseClicks();
    document.removeEventListener('keydown', onCancelKey);
  };
  const cancel = (): void => {
    cancelled = true; releaseLoading();
    if (cancelPendingEdit === cancel) cancelPendingEdit = null;
  };
  cancelPendingEdit = cancel;
  void loadCountry(entity).then((all) => {
    if (cancelled) return;
    releaseLoading();
    if (cancelPendingEdit === cancel) cancelPendingEdit = null;
    const terrs: Terr[] = all.map((e) => ({
      entity: e, wasPolygon: e.geometry?.type === 'Polygon', rings: ringsOf(e.geometry), env: null,
    })).filter((t) => t.rings.length);
    if (!terrs.length) { toast('This territory has no outline to drag.'); hooks.onDone(); return; }
    startEdit(map, terrs, String(entity.attrs.country ?? entity.name ?? 'territory'),
              String(entity.attrs.country_iso ?? '').toUpperCase(), hooks);
  }).catch((err: unknown) => {
    // A failed load must not leave the "Loading boundaries" bar holding the
    // map's input.
    if (cancelled) return;
    releaseLoading();
    if (cancelPendingEdit === cancel) cancelPendingEdit = null;
    toast(err instanceof Error ? err.message : 'Could not load the boundaries.');
    hooks.onDone();
  });
}

function startEdit(map: maplibregl.Map, terrs: Terr[], title: string,
                   iso: string, hooks: EditHooks): void {
  const finish = async (): Promise<void> => {
    const s = state;
    if (!s || s.saving || !s.steps.length) return;
    const items = s.terrs.filter((t) => t.env).map((t) => ({
      id: t.entity.id, geometry: geometryOf(t.rings, t.wasPolygon), changed: t.env,
    }));
    if (!items.length) return;
    s.saving = true;
    select(map, null);
    s.bar.update(s.steps.length, 'Saving…');
    s.bar.setBusy(true);
    try {
      const res = await post<ReshapeResult>('/api/territory/reshape', { territories: items });
      if (res.rev) bumpTiles(map, res.rev); else forceTileRefresh(map);
      toast(summary(res));
      s.cleanup();
      hooks.onSaved(res);
      hooks.onDone();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not move the boundary.');
      s.saving = false;
      s.bar.setBusy(false);
      s.bar.update(s.steps.length);
    }
  };
  const bar = showDrawBar({
      surface: map.getCanvas(),
    title: `Reshape boundaries — ${title}`,
    finishLabel: 'Save boundary',
    hint: 'Move corners to reshape existing territory. Push moves a stretch of the border; Trace follows a guide line. Save boundary applies the change.',
    minPoints: 1,
    minHint: 'Drag a vertex · click the line to add one · tap or right-click a vertex to delete it',
    onFinish: () => { void finish(); },
    onUndo: () => undo(map),
    onCancel: () => { cancelTerritoryEdit(); hooks.onDone(); },
    extras: [
      { key: 'vertices', label: '⤢ Move corners', on: true,
        onClick: () => { setPushing(map, false); setTracing(map, false); } },
      { key: 'snap', advanced: true, label: '⌁ Snap', on: true,
        title: 'Snap a dragged vertex onto a neighbouring outline or the '
             + 'country boundary. Hold Alt to place one freely.',
        onClick: () => setSnapping(map, !state?.snapping) },
      { key: 'trace', label: '⇝ Trace guide',
        title: 'Click two points on a line — or on lines that meet — and the boundary '
             + 'adopts the whole run between them.',
        onClick: () => setTracing(map, !state?.tracing) },
      { key: 'push', label: '◐ Push border',
        title: 'Brush the line one way or the other: every vertex under the brush moves '
             + 'with it, on both sides of a shared boundary. With Snap on, the pushed line '
             + 'clings to the boundaries and roads it meets (Alt for none).',
        onClick: () => setPushing(map, !state?.pushing) },
      { key: 'radius', label: `◌ ${BRUSH_RADII[2]}px`, title: 'Push brush size — also [ and ]',
        onClick: () => stepRadius(map, 1, true) },
      { key: 'admin', advanced: true, label: '▦ Admin',
        title: 'Admin boundaries to snap and trace to: off, then each level the country '
             + 'has — detailed where loaded',
        onClick: () => { state?.lines?.cycleLevel(); } },
      { key: 'roads', advanced: true, label: '⛙ Roads',
        title: 'Snap and trace to roads (OpenStreetMap, fetched for the view)',
        onClick: () => {
          const lines = state?.lines;
          if (!lines) return;
          lines.setRoads(!lines.roads());
          state?.bar.setExtra('roads', { on: lines.roads() });
        } },
    ],
  });
  bar.setExtra('radius', { hidden: true });
  const onKey = (e: KeyboardEvent) => {
    if (!state || state.saving) return;
    const typing = (document.activeElement as HTMLElement | null)?.matches?.('input, textarea, select');
    if (typing) return;
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
      e.preventDefault(); undo(map); return;
    }
    if (e.key === 'Escape') {
      if (state.pushing) { setPushing(map, false); return; }
      if (state.traceFrom) { state.traceFrom = null; paintSnapMark(map, null); return; }
      if (state.tracing) { setTracing(map, false); return; }
      if (state.selected) { select(map, null); return; }
      cancelTerritoryEdit();
      hooks.onDone();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && state.selected && !typing) {
      e.preventDefault();
      deleteVertex(map, state.selected);
    } else if ((e.key === '[' || e.key === ']') && state.pushing && !typing) {
      e.preventDefault();
      stepRadius(map, e.key === ']' ? 1 : -1, false);
    }
  };
  document.addEventListener('keydown', onKey);
  state = {
    terrs, index: buildIndex(terrs), steps: [], bar, drag: null, selected: null, crowded: 0,
    saving: false, guides: null, snapIndex: null, snapping: true, tracing: false,
    traceFrom: null, lines: null, guidesStale: false, pushing: false,
    radiusPx: BRUSH_RADII[2]!,
    cleanup: () => {
      bar.close();
      state?.lines?.destroy();
      hideBrush();
      document.removeEventListener('keydown', onKey);
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
      chip?.remove();
      chip = null;
      state = null;
      countryRings = [];
      paintShape(map);
      paintHandles(map);
      (map.getSource(SRC_DRAG) as maplibregl.GeoJSONSource | undefined)?.setData(EMPTY);
      (map.getSource(SRC_GUIDE) as maplibregl.GeoJSONSource | undefined)?.setData(EMPTY);
      if (map.getLayer(L_GUIDE)) map.setLayoutProperty(L_GUIDE, 'visibility', 'none');
    },
  };
  // Admin linework and roads for the view, re-fetched as the view moves. The
  // snapper re-indexes when lines land — after the drop, if a vertex is in
  // hand, since the index must not change under a drag.
  state.lines = createGuideSet(map, {
    iso,
    onChange: () => {
      const s = state;
      if (!s?.lines) return;
      if (s.drag) s.guidesStale = true; else invalidateGuides();
      s.bar.setExtra('admin', { label: s.lines.levelLabel(), on: !!s.lines.level() });
      const note = s.lines.status();
      if (note && !s.drag) s.bar.update(s.steps.length, note);
    },
  });
  // The country outline arrives after the editor is up: snapping works
  // against the other outlines meanwhile, and gains the coast when it lands.
  void loadOutline(map, iso);
  paintShape(map);
  paintHandles(map);
  toast(map.getZoom() < HANDLE_MINZOOM
    ? 'Zoom in to a line to drag its vertices. Esc cancels.'
    : 'Drag the vertices; click a line to add one; tap a vertex to delete it. '
      + 'Vertices snap to the lines around them — Alt places one freely, and '
      + '⇝ Trace follows a run of an existing line. Esc cancels.');
}

/** Drop an edit in progress without saving. Safe when none is. */
export function cancelTerritoryEdit(): void {
  cancelPendingEdit?.();
  state?.cleanup();
}

/** What the server did with the move, in words. */
export function summary(res: ReshapeResult): string {
  const parts: string[] = [];
  if (res.gainedKm2 > 0) parts.push(`took ${fmtKm2(res.gainedKm2)}`);
  if (res.vacatedKm2 > 0) {
    const handed = res.vacatedKm2 - res.releasedKm2;
    if (handed > 0.05) parts.push(`handed ${fmtKm2(handed)} to the neighbour`);
    if (res.releasedKm2 > 0.05) parts.push(`released ${fmtKm2(res.releasedKm2)} to nobody`);
  }
  if (res.removed.length) {
    parts.push(`${res.removed.length} territor${res.removed.length === 1 ? 'y' : 'ies'} swallowed whole`);
  }
  return parts.length ? `Boundary moved: ${parts.join(', ')}.` : 'Boundary unchanged.';
}

// --- the cut: capturing a line, showing the pieces --------------------------------

/** Click a series of points; Finish resolves the line, Cancel/Esc null. The
 *  same shape main.ts's captureArea has, on the editor's own source. */
export interface CaptureLineOpts {
  /** The country whose admin levels the guide cycle offers. */
  iso?: string;
  /** Territory outlines to snap to — a cut usually starts and ends on one. */
  outlines?: EntityDto[];
}

/** The most points a line is sent with (SplitBody's ceiling on the server).
 *  A run traced along detailed ADM3 lines and roads can pass it. */
export const LINE_MAX_PTS = 20_000;

/** Thin a line to at most `max` points, evenly, keeping both ends. */
export function thinLine(line: Pt[], max = LINE_MAX_PTS): Pt[] {
  if (line.length <= max || max < 2) return line;
  const out: Pt[] = [];
  const step = (line.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) out.push(line[Math.round(i * step)]!);
  return out;
}

/** Capture a line by clicks — the split's new boundary. The corners SNAP to
 *  the lines in view (territory outlines, the admin level the bar's ▦ cycles
 *  through, roads with ⛙; Alt places one freely) and, with ⇝ Follow, two
 *  snapped corners in a row are joined ALONG those lines rather than by a
 *  straight edge — so a front drawn down a road or a district boundary IS
 *  that road or boundary. The same guides and snapper the area gesture and
 *  the boundary editor use (guides.ts, snap.ts). */
export function captureLine(map: maplibregl.Map, title: string,
                            opts: CaptureLineOpts = {}): Promise<Pt[] | null> {
  if (!map.getSource(SRC_CUT)) installTerritoryEditor(map);
  installGuideLayers(map);
  return new Promise((resolve) => {
    interface Corner { pt: Pt; run: Pt[]; hit: SnapHit | null; index: SnapIndex | null }
    const corners: Corner[] = [];
    let snapping = true;
    let follow = false;
    let cursor: Pt | null = null;
    let cursorHit: SnapHit | null = null;
    let index: SnapIndex | null = null;
    const src = map.getSource(SRC_CUT) as maplibregl.GeoJSONSource;
    const outlineRings: Pt[][] = (opts.outlines ?? [])
      .flatMap((e) => ringsOf(e.geometry).map(flatRing));

    const lines = createGuideSet(map, {
      iso: opts.iso ?? '',
      onChange: () => {
        index = null;
        bar.setExtra('admin', { label: lines.levelLabel(), on: !!lines.level() });
        update();
      },
    });
    const ensureIndex = (): SnapIndex => {
      if (index) return index;
      const project = (p: Pt): ScreenPt => {
        const q = map.project(p as [number, number]);
        return { x: q.x, y: q.y };
      };
      const guides: Guide[] = [];
      let total = 0;
      const add = (pts: Pt[], closed: boolean, source: Guide['source']): void => {
        if (pts.length < 2 || total + pts.length > SNAP_MAX_PTS) return;
        total += pts.length;
        guides.push({ pts, screen: pts.map(project), closed, source });
      };
      // Guides first: they are cut to the view and are what a front moves
      // onto; a country's worth of outline must not crowd them out.
      for (const l of lines.lines()) add(l.pts, false, l.source);
      for (const r of outlineRings) add(r, true, 'territory');
      index = buildSnapIndex(guides);
      return index;
    };
    const snapAt = (x: number, y: number): SnapHit | null =>
      (snapping ? nearestSnap(ensureIndex(), { x, y }, SNAP_PX) : null);
    /** A corner's hit in the CURRENT index: the camera may have moved since. */
    const hitFor = (c: Corner): SnapHit | null => {
      if (!c.hit) return null;
      const idx = ensureIndex();
      if (c.index === idx) return c.hit;
      const q = map.project(c.pt as [number, number]);
      return nearestSnap(idx, { x: q.x, y: q.y }, 1.5);
    };
    const joined = (a: SnapHit | null, b: SnapHit | null): Pt[] | null => {
      if (!follow || !a || !b) return null;
      // Territory outlines only at the two ends: a country's worth of
      // vertices is no network to route across.
      return traceNetwork(ensureIndex().guides, a, b, (g) => g.source !== 'territory');
    };
    const pathOf = (): Pt[] => corners.flatMap((c) => [...c.run, c.pt]);

    const paint = (): void => {
      const feats: GeoJSON.Feature[] = [];
      const path = pathOf();
      const line = cursor && path.length ? [...path, cursor] : path;
      if (line.length >= 2) {
        feats.push({ type: 'Feature', properties: {},
          geometry: { type: 'LineString', coordinates: line } });
      }
      for (const c of corners) {
        feats.push({ type: 'Feature', properties: { role: 'corner' },
          geometry: { type: 'Point', coordinates: c.pt } });
      }
      if (cursorHit) {
        feats.push({ type: 'Feature', properties: { role: 'snap' },
          geometry: { type: 'Point', coordinates: cursorHit.pos } });
      }
      src.setData({ type: 'FeatureCollection', features: feats });
    };
    const hint = (): string =>
      `${snapping ? 'Corners snap to outlines, admin lines and roads; Alt places one freely.' : 'Click the corners of the line.'}${
        follow ? ' Snapped corners are joined along the lines.' : ''} Draw from outside one edge of the territory to outside the other, then Preview split. Backspace undoes.`;
    const update = (): void => {
      const n = pathOf().length;
      const readout = [n > corners.length ? `${corners.length} corners · ${n} points` : '',
                       lines.status()].filter(Boolean).join(' · ');
      bar.setHint(hint());
      bar.update(corners.length, readout || undefined);
    };

    otherMode++;
    const canvas = map.getCanvas();
    canvas.style.cursor = 'crosshair';
    const wasDoubleZoom = map.doubleClickZoom.isEnabled();
    map.doubleClickZoom.disable();
    let done = false;
    const cleanup = (): void => {
      bar.close();
      lines.destroy();
      map.off('click', onClick);
      map.off('mousemove', onMove);
      map.off('dblclick', onDbl);
      map.off('moveend', onMoveEnd);
      document.removeEventListener('keydown', onKey);
      if (wasDoubleZoom) map.doubleClickZoom.enable();
      canvas.style.cursor = '';
      otherMode--;
      src.setData(EMPTY);
      if (!done) resolve(null);
    };
    const finish = (): void => {
      if (corners.length < 2) return;
      done = true;
      const line = thinLine(pathOf());
      cleanup();
      resolve(line);
    };
    const bar = showDrawBar({
      surface: canvas,
      title, minPoints: 2, pointShortcuts: true, finishLabel: 'Preview split',
      hint: hint(),
      minHint: 'Click from outside one edge to outside the other',
      onFinish: finish,
      onUndo: () => { corners.pop(); paint(); update(); },
      onCancel: cleanup,
      extras: [
        { key: 'snap', label: '⌁ Snap to lines', on: true,
          title: 'Corners snap to the lines under them — territory outlines, admin boundaries, roads. Alt places one freely.',
          onClick: () => { snapping = !snapping; if (!snapping) follow = false; bar.setExtra('snap', { on: snapping }); bar.setExtra('follow', { on: follow }); update(); } },
        { key: 'follow', label: '⇝ Follow lines', on: false,
          title: 'Two snapped corners in a row are joined along the lines between them — a road, a district boundary — not by a straight edge',
          onClick: () => { follow = !follow; if (follow) snapping = true; bar.setExtra('follow', { on: follow }); bar.setExtra('snap', { on: snapping }); update(); } },
        { key: 'admin', advanced: true, label: '▦ Admin',
          title: 'Admin boundaries to snap to and follow: off, then each level the country has — detailed where loaded',
          onClick: () => { lines.cycleLevel(); } },
        { key: 'roads', advanced: true, label: '⛙ Roads', title: 'Snap to and follow roads (OpenStreetMap, fetched for the view)',
          onClick: () => { lines.setRoads(!lines.roads()); bar.setExtra('roads', { on: lines.roads() }); } },
      ],
    });
    const onClick = (e: maplibregl.MapMouseEvent): void => {
      if (e.originalEvent.detail > 1) return;
      const alt = (e.originalEvent as MouseEvent).altKey;
      const hit = alt ? null : snapAt(e.point.x, e.point.y);
      const pt: Pt = hit ? [hit.pos[0]!, hit.pos[1]!] : [e.lngLat.lng, e.lngLat.lat];
      let run: Pt[] = [];
      const prev = corners[corners.length - 1];
      if (prev && hit) {
        const path = joined(hitFor(prev), hit);
        if (path && path.length > 2) run = path.slice(1, -1);
        else if (follow && prev.hit && !path) {
          toast('Those corners are not joined along the lines in view — a straight edge instead.');
        }
      }
      if (prev && prev.pt[0] === pt[0] && prev.pt[1] === pt[1]) return;
      corners.push({ pt, run, hit, index: hit ? index : null });
      paint();
      update();
    };
    const onMove = (e: maplibregl.MapMouseEvent): void => {
      const alt = (e.originalEvent as MouseEvent).altKey;
      cursorHit = alt ? null : snapAt(e.point.x, e.point.y);
      cursor = cursorHit ? [cursorHit.pos[0]!, cursorHit.pos[1]!] : [e.lngLat.lng, e.lngLat.lat];
      if (corners.length || cursorHit) paint();
    };
    const onDbl = (e: maplibregl.MapMouseEvent): void => { e.preventDefault(); finish(); };
    const onMoveEnd = (): void => { index = null; };
    const onKey = (e: KeyboardEvent): void => {
      const typing = (document.activeElement as HTMLElement | null)?.matches?.('input, textarea, select');
      if (!typing && e.key === 'Escape') cleanup();
    };
    map.on('click', onClick);
    map.on('mousemove', onMove);
    map.on('dblclick', onDbl);
    map.on('moveend', onMoveEnd);
    document.addEventListener('keydown', onKey);
    update();
  });
}

export interface PieceDto {
  index: number; geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon; km2: number;
  lon: number; lat: number;
  neighbour: { id: string; name: string | null; faction: string | null;
    status: string | null; contestedBy: string | null } | null;
}

/** Draw the cut's pieces, numbered, with `picked` highlighted; clicking a
 *  piece calls `onPick`. Returns a function that clears everything. */
export function showPieces(map: maplibregl.Map, line: Pt[], pieces: PieceDto[], picked: number,
                           onPick: (index: number) => void): () => void {
  if (!map.getSource(SRC_PIECES)) installTerritoryEditor(map);
  const src = map.getSource(SRC_PIECES) as maplibregl.GeoJSONSource;
  const cut = map.getSource(SRC_CUT) as maplibregl.GeoJSONSource;
  const paint = (pick: number): void => {
    const feats: GeoJSON.Feature[] = [];
    for (const p of pieces) {
      feats.push({ type: 'Feature', properties: { index: p.index, picked: p.index === pick ? 1 : 0 },
        geometry: p.geometry });
      feats.push({ type: 'Feature', properties: { label: String(p.index + 1) },
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] } });
    }
    src.setData({ type: 'FeatureCollection', features: feats });
  };
  cut.setData({ type: 'FeatureCollection', features: [
    { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line } },
  ] });
  paint(picked);
  otherMode++;
  const onClick = (e: maplibregl.MapMouseEvent): void => {
    const hit = map.queryRenderedFeatures(e.point, { layers: [L_PIECE_FILL] })[0];
    if (!hit) return;
    const index = Number(hit.properties.index);
    paint(index);
    onPick(index);
  };
  map.on('click', onClick);
  let cleared = false;
  return () => {
    if (cleared) return;
    cleared = true;
    map.off('click', onClick);
    otherMode--;
    src.setData(EMPTY);
    cut.setData(EMPTY);
  };
}

/** Which of `territories` a drawn line cuts: the one holding the middle of
 *  any of its segments (a cut runs from outside to outside, so the ends say
 *  nothing). First hit wins. */
export function territoryUnderLine(territories: EntityDto[], line: Pt[]): EntityDto | null {
  for (let k = 0; k + 1 < line.length; k++) {
    const a = line[k]!;
    const b = line[k + 1]!;
    const mid: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const hit = territories.find((t) => containsPoint(t.geometry, mid));
    if (hit) return hit;
  }
  return null;
}
